import React, { memo, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { formatDate } from '@utils/formatters'

/**
 * ReportHistory - Dropdown to select from historical reports
 *
 * @param {Array} history - Array of historical report summaries
 * @param {number|null} selectedId - Currently selected report ID (null for latest)
 * @param {Function} onSelect - Callback when report is selected
 */
const ReportHistory = memo(({ history = [], selectedId, onSelect }) => {
	const [isOpen, setIsOpen] = useState(false)
	const dropdownRef = useRef(null)

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false)
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	if (!history || history.length === 0) {
		return null
	}

	const handleSelect = (reportId) => {
		onSelect(reportId)
		setIsOpen(false)
	}

	const selectedReport = selectedId
		? history.find(r => r.id === selectedId)
		: null

	const displayLabel = selectedReport
		? formatDate(selectedReport.generation_completed_at || selectedReport.created_at, 'PP')
		: 'Latest Report'

	return (
		<div className="report-history" ref={dropdownRef}>
			<button
				className="report-history__trigger"
				onClick={() => setIsOpen(!isOpen)}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
			>
				<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
					<path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>
				<span>History</span>
				<svg className={`report-history__arrow ${isOpen ? 'report-history__arrow--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
					<path d="M19 9l-7 7-7-7" />
				</svg>
			</button>

			{isOpen && (
				<div className="report-history__dropdown" role="listbox">
					<div
						className={`report-history__option ${!selectedId ? 'report-history__option--selected' : ''}`}
						onClick={() => handleSelect('latest')}
						role="option"
						aria-selected={!selectedId}
					>
						<span className="report-history__option-label">Latest Report</span>
						{!selectedId && (
							<svg className="report-history__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M5 13l4 4L19 7" />
							</svg>
						)}
					</div>

					<div className="report-history__divider" />

					{history.slice(0, 5).map((report) => (
						<div
							key={report.id}
							className={`report-history__option ${selectedId === report.id ? 'report-history__option--selected' : ''}`}
							onClick={() => handleSelect(report.id)}
							role="option"
							aria-selected={selectedId === report.id}
						>
							<div className="report-history__option-content">
								<span className="report-history__option-label">
									{formatDate(report.generation_completed_at || report.created_at, 'PPp')}
								</span>
								<span className="report-history__option-meta">
									{report.total_active_projects} projects
								</span>
							</div>
							{selectedId === report.id && (
								<svg className="report-history__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<path d="M5 13l4 4L19 7" />
								</svg>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	)
})

ReportHistory.displayName = 'ReportHistory'

ReportHistory.propTypes = {
	history: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			total_active_projects: PropTypes.number,
			generation_completed_at: PropTypes.string,
			created_at: PropTypes.string
		})
	),
	selectedId: PropTypes.number,
	onSelect: PropTypes.func.isRequired
}

export default ReportHistory
